import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { Search, MapPin, Sparkles } from "lucide-react";
import { disciplineColor, DISCIPLINE_KEYS } from "@/lib/disciplines";

const DISCIPLINES = ["all", ...DISCIPLINE_KEYS];
const CITIES = ["all","mumbai","delhi","bangalore","chennai","kolkata","hyderabad","ahmedabad","kochi"];

export default function Discovery() {
  const [discipline, setDiscipline] = useState("all");
  const [city, setCity] = useState("all");
  const [search, setSearch] = useState("");
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get("/artists", { params: { discipline, city, search } }).then(({ data }) => {
      if (!cancel) { setArtists(data.artists); setLoading(false); }
    });
    return () => { cancel = true; };
  }, [discipline, city, search]);

  return (
    <PageShell>
      <section className="relative border-b border-border grid-bg">
        <SlideCorner right="THE ROSTER" />
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-12">
          <SectionLabel>The Discovery Project</SectionLabel>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[7rem] font-bold leading-[0.9] tracking-tighter">
            India's <span className="hl-cyan">roster.</span> <br /> In one place.
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-base md:text-lg leading-relaxed">
            We go on the road. We meet artists in their studios, rehearsal rooms, basements, balconies. We photograph, interview, and verify — so you don't have to. Searchable by discipline, city, aesthetic. <span className="font-medium text-white">Click any profile to learn more.</span>
          </p>
        </div>
      </section>

      <section className="border-b border-border sticky top-14 z-30 bg-background/95 backdrop-blur">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-5 flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[240px] flex items-center gap-3 border border-border px-4 py-3">
            <Search size={16} className="text-primary" />
            <input data-testid="discovery-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, aesthetic, bio…" className="flex-1 bg-transparent outline-none text-sm" />
          </div>
          <Select label="Discipline" value={discipline} onChange={setDiscipline} options={DISCIPLINES} testid="discovery-discipline-select" />
          <Select label="City" value={city} onChange={setCity} options={CITIES} testid="discovery-city-select" />
        </div>
      </section>

      <section>
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12">
          <div className="flex items-baseline justify-between mb-8">
            <p className="meta-mono">— {artists.length} ARTISTS · VERIFIED ROSTER</p>
            <p className="meta-mono">SORT / FEATURED</p>
          </div>
          {loading ? (
            <p className="font-mono text-sm text-white/60">Loading roster…</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-border">
              {artists.map((a) => <ArtistCard key={a.profile_id || a.name} a={a} />)}
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}

function ArtistCard({ a }) {
  const slug = (a.profile_id?.startsWith("seed_") ? a.name : (a.profile_id || a.name))?.toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const color = disciplineColor(a.discipline);
  return (
    <Link to={`/artist/${slug}`} data-testid={`artist-card-${a.profile_id || a.name}`} className="bg-background p-5 hover:bg-white/[0.03] transition group relative">
      {/* Left edge stripe in the discipline color */}
      <span className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: color }} />
      <div className="aspect-[4/5] bg-muted overflow-hidden mb-4">
        {a.profile_image_url ? (
          <img src={a.profile_image_url} alt={a.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-700" />
        ) : (
          <div className="w-full h-full grid place-items-center text-white/30 font-display text-5xl">{a.name?.[0]}</div>
        )}
      </div>
      <div className="flex items-center justify-between mb-1.5">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] flex items-center gap-1.5" style={{ color }}>
          <span style={{ width: 6, height: 6, background: color, display: "inline-block" }} />
          {a.discipline?.replace("_", " ")}
        </p>
        {a.verified && <Sparkles size={11} className="text-cream" />}
      </div>
      <p className="font-display text-2xl font-bold leading-tight">{a.name}</p>
      <p className="meta-mono mt-2 flex items-center gap-1.5"><MapPin size={10} /> {a.city}</p>
      {a.aesthetic && <p className="font-serif-italic text-white/70 text-sm mt-3">{a.aesthetic}</p>}
    </Link>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-2 border border-border">
      <span className="meta-mono pl-3 pr-2 border-r border-border py-3">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="bg-transparent outline-none text-sm pr-4 py-3 font-mono uppercase">
        {options.map((o) => <option key={o} value={o} className="bg-background">{o.replace("_", " ")}</option>)}
      </select>
    </div>
  );
}
